import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Scan, CircleNotch } from "@phosphor-icons/react";
import { useLang } from "../context/LangContext";
import api from "../lib/api";

export const ScansModal = ({ machine, onClose }) => {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/machines/${machine.id}/scans`).then(({ data }) => setData(data)).catch(() => setData({ count: 0, scans: [] }));
  }, [machine.id]);

  const fmt = (ts) => new Date(ts).toLocaleString(lang === "it" ? "it-IT" : "en-GB", { dateStyle: "medium", timeStyle: "short" });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0F1A]/50 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-md border border-slate-200 max-h-[85vh] flex flex-col" data-testid="scans-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-display font-bold text-xl text-[#0F172A]">{t.scans.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{machine.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-[#0F172A]" data-testid="scans-modal-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          {!data ? (
            <div className="flex justify-center py-10"><CircleNotch size={26} className="text-emerald-500 animate-spin" /></div>
          ) : data.count === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="scans-empty">{t.scans.empty}</p>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="scans-count">
                <Scan size={18} className="text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-800">{data.count} {data.count === 1 ? t.scans.one : t.scans.badge}</span>
              </div>
              <p className="mt-3 text-xs text-slate-500">{t.scans.sub}</p>
              {data.daily && (
                <div className="mt-5" data-testid="scans-week-chart">
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.scans.week}</p>
                  <div className="mt-2 flex items-end gap-1.5 h-24">
                    {(() => {
                      const max = Math.max(...data.daily.map((d) => d.count), 1);
                      return data.daily.map((d) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                          <span className="font-mono-tech text-[9px] text-slate-500 h-3">{d.count > 0 ? d.count : ""}</span>
                          <div className={`w-full rounded-sm ${d.count > 0 ? "bg-emerald-500" : "bg-slate-200"}`}
                            style={{ height: `${d.count > 0 ? Math.max((d.count / max) * 60, 8) : 3}px` }} />
                          <span className="font-mono-tech text-[9px] text-slate-400">{new Date(d.date + "T00:00:00").toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { weekday: "short" })}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
              <ul className="mt-4 divide-y divide-slate-100" data-testid="scans-list">
                {data.scans.map((s) => (
                  <li key={s.id} className="flex items-center gap-2.5 py-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="font-mono-tech text-xs text-slate-600">{fmt(s.ts)}</span>
                    {(s.city || s.country) && (
                      <span className="ml-auto text-[11px] text-slate-400">{[s.city, s.country].filter(Boolean).join(", ")}</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
